import React, { useCallback } from "react";
import styled from "styled-components";
import DualListBox from "react-dual-listbox";
import { FormControl, FormLabel, Button } from "react-bootstrap";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useFieldArray, UseFormReturn } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { FunctionalityReportFormType } from "../index";
import { useUserPropertyTickers } from "../../../components/user-tickers-providers";
import { ErrorFieldMessage } from "../shared/components/ErrorFieldMessage";

const messages = defineMessages({
  title: {
    id: "functionalityReport.group.title",
    defaultMessage: "Properties",
  },
  addGroup: {
    id: "functionalityReport.group.addGroup",
    defaultMessage: "+ Add Group",
  },
  group: {
    id: "functionalityReport.group.group",
    defaultMessage: "Group {idx}",
  },
  leftListHeader: {
    id: "functionalityReport.group.leftListHeader",
    defaultMessage: "Available Hotel Tickers",
  },
  rightListHeader: {
    id: "functionalityReport.group.rightListHeader",
    defaultMessage: "Selected Hotel Tickers",
  },
  groupNamePlaceholder: {
    id: "functionalityReport.group.groupNamePlaceholder",
    defaultMessage: "Enter group name",
  },
});

const WitLabel = styled.label`
  display: block;
  width: 100%;
  padding: 0;
  margin: 10px 0 0 0;
  font-size: 21px;
  line-height: inherit;
  color: #3a3f51;
  border: 0;
  border-bottom: 1px solid #e5e5e5;
`;

export function FunctionalityGroup({
  getValues,
  setValue,
  register,
  control,
  formState: { errors },
  watch,
}: UseFormReturn<FunctionalityReportFormType>) {
  const intl = useIntl();
  const hotelTickers = useUserPropertyTickers();

  const { fields } = useFieldArray({ name: "groups", control });

  const handleChange = useCallback(
    (tickers: string[], idx: number) => {
      setValue(`groups.${idx}.hotelTickers`, tickers, { shouldValidate: true });
    },
    [setValue]
  );

  const handleAddGroup = useCallback(() => {
    const groups = getValues("groups") || [];
    setValue("groups", [...groups, { hotelTickers: [] }]);
  }, [getValues, setValue]);

  const handleRemoveGroup = useCallback(() => {
    const groups = getValues("groups") || [];
    if (groups.length > 1) {
      setValue("groups", groups.slice(0, -1));
    }
  }, [getValues, setValue]);

  const canBeRemoved = useCallback(
    (idx: number) => {
      return idx !== 0 && idx === watch("groups").length - 1;
    },
    [watch]
  );

  return (
    <>
      <WitLabel>
        <FormattedMessage {...messages.title} />
        <Button className="btn-sm ml-3 mb-1" onClick={handleAddGroup}>
          <FormattedMessage {...messages.addGroup} />
        </Button>
      </WitLabel>
      {fields.map((field, idx) => (
        <div className="flex flex-col gap-2 pt-2" key={`group-container-${field.id}`}>
          <FormLabel className="font-weight-bold text-lg">
            <FormattedMessage {...messages.group} values={{ idx: idx + 1 }} />
            {canBeRemoved(idx) && (
              <Button className="ml-3 btn-sm" onClick={handleRemoveGroup}>
                <FontAwesomeIcon icon={faTrashCan} />
              </Button>
            )}
          </FormLabel>
          <FormControl
            type="text"
            key={`group-name-${field.id}`}
            {...register(`groups.${idx}.name`)}
            placeholder={intl.formatMessage(messages.groupNamePlaceholder)}
          />
          <ErrorFieldMessage errors={errors} name={`groups[${idx}].name`} />
          <DualListBox
            {...register(`groups.${idx}.hotelTickers`)}
            key={`group-hotel-tickers-${field.id}`}
            options={hotelTickers.map(it => ({ label: it, value: it }))}
            onChange={(tickers: string[]) => handleChange(tickers, idx)}
            showHeaderLabels
            canFilter
            selected={watch(`groups.${idx}.hotelTickers`)}
            lang={{
              availableHeader: intl.formatMessage(messages.leftListHeader),
              selectedHeader: intl.formatMessage(messages.rightListHeader),
            }}
          />
          <ErrorFieldMessage errors={errors} name={`groups[${idx}].hotelTickers`} />
        </div>
      ))}
    </>
  );
}
